// ═══════════════════════════════════════════════════════════════
// MainStreet — Status Checks (Create + Manage + Respond)
// Merged StatusCheckIn + StatusCheckManager + AdminStatusResults
// ═══════════════════════════════════════════════════════════════

import { useEffect, useState, useMemo } from 'react';
import {
  Title, Text, Paper, Group, Badge, Button, TextInput, Textarea,
  Select, MultiSelect, Modal, Table, Collapse, ActionIcon, Loader,
} from '../../components/gab/GabComponents';
import { useDisclosure } from '../../components/gab/GabHooks';
import { notifications } from '../../lib/notifications';
import {
  IconPlus, IconRefresh, IconChevronDown, IconChevronUp, IconSend,
  IconClipboardCheck, IconLock,
} from '@tabler/icons-react';
import { useMainStreetOpsStore } from '../../store/mainstreetOpsStore';
import type { MSStatusCheck, MSStatusResponse } from '../../store/mainstreetOpsStore';
import { useMainStreetStore } from '../../store/mainstreetStore';
import { useAuthStore } from '../../store/authStore';
import { SECTOR_TYPES } from './constants';
import { fmtDate, fmtDateTime } from '../../lib/dateFormat';
import { useModulePermission } from '../../hooks/useModulePermission';

// ── Helpers ──────────────────────────────────────────────────

const SECTOR_OPTIONS = SECTOR_TYPES.map(s => ({ value: s.id, label: s.label }));

// ═════════════════════════════════════════════════════════════
export default function StatusChecks() {
  const { canEdit } = useModulePermission('mainstreet');
  const {
    statusChecks, statusResponses, loading,
    loadStatusChecks, loadStatusResponses, createStatusCheck, submitResponse,
    updateStatusCheck,
  } = useMainStreetOpsStore();
  const { organizations, loadOrganizations, orgLists, loadOrgLists } = useMainStreetStore();
  const { activeIncident, user } = useAuthStore();

  // Create modal
  const [createOpened, { open: openCreate, close: closeCreate }] = useDisclosure(false);
  const [title, setTitle] = useState('');
  const [question, setQuestion] = useState('');
  const [targetType, setTargetType] = useState('all');
  const [targetSectors, setTargetSectors] = useState<string[]>([]);
  const [targetOrgs, setTargetOrgs] = useState<string[]>([]);
  const [targetList, setTargetList] = useState<string | null>(null);
  const [deadline, setDeadline] = useState('');
  const [saving, setSaving] = useState(false);

  // Expanded checks
  const [expandedIds, setExpandedIds] = useState<Record<number, boolean>>({});

  // Respond form per check
  const [respondCheckId, setRespondCheckId] = useState<number | null>(null);
  const [respOrgId, setRespOrgId] = useState('');
  const [respText, setRespText] = useState('');
  const [respNotes, setRespNotes] = useState('');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    loadStatusChecks();
    loadStatusResponses();
    loadOrganizations();
    loadOrgLists();
  }, []);

  const orgOptions = useMemo(() =>
    organizations.map(o => ({ value: String(o.id), label: o.name })),
    [organizations],
  );

  const filtered = useMemo(() =>
    [...statusChecks].sort((a, b) => (b.createdAt ?? '').localeCompare(a.createdAt ?? '')),
  [statusChecks]);

  function responsesForCheck(checkId: number): MSStatusResponse[] {
    return statusResponses.filter(r => String(r.statusCheckId) === String(checkId));
  }

  function targetCount(check: MSStatusCheck): number {
    if (check.targetType === 'specific' && check.targetOrgs) {
      return check.targetOrgs.split(',').filter(Boolean).length;
    }
    if (check.targetType === 'sector' && check.targetSectors) {
      const sectors = check.targetSectors.split(',').filter(Boolean);
      return organizations.filter(o => sectors.includes(o.sectorType)).length;
    }
    return organizations.length;
  }

  async function handleClose(check: MSStatusCheck) {
    await updateStatusCheck(check.id, { status: 'closed' });
    notifications.show({ color: '#939598', title: 'Closed', message: `"${check.title}" closed.` });
  }

  function toggleExpand(id: number) {
    setExpandedIds(prev => ({ ...prev, [id]: !prev[id] }));
  }

  // ── Create ─────────────────────────────────────────────────

  async function handleCreate() {
    if (!title.trim() || !question.trim()) {
      notifications.show({ color: 'var(--color-error)', title: 'Required', message: 'Title and question are required.' });
      return;
    }
    setSaving(true);
    // Resolve list to specific org IDs
    let resolvedTargetType = targetType;
    let resolvedTargetOrgs = targetOrgs.join(',');
    if (targetType === 'list' && targetList) {
      const list = orgLists.find(l => String(l.id) === targetList);
      if (list) {
        resolvedTargetType = 'specific';
        resolvedTargetOrgs = list.orgIds;
      }
    }

    await createStatusCheck({
      incidentId: activeIncident ? String(activeIncident.id) : '',
      title: title.trim(),
      question: question.trim(),
      targetType: resolvedTargetType,
      targetSectors: targetSectors.join(','),
      targetOrgs: resolvedTargetOrgs,
      deadline,
      status: 'open',
      createdAt: new Date().toISOString(),
      authorName: user?.fullName ?? user?.email ?? 'Unknown',
    });
    setSaving(false);
    notifications.show({ color: 'var(--color-success)', title: 'Created', message: 'Status check created.' });
    setTitle(''); setQuestion(''); setTargetType('all'); setTargetSectors([]);
    setTargetOrgs([]); setDeadline('');
    closeCreate();
  }

  // ── Respond ────────────────────────────────────────────────

  async function handleRespond(check: MSStatusCheck) {
    if (!respOrgId || !respText.trim()) {
      notifications.show({ color: 'var(--color-error)', title: 'Required', message: 'Select org and provide response.' });
      return;
    }
    const org = organizations.find(o => String(o.id) === respOrgId);
    setSubmitting(true);
    await submitResponse({
      statusCheckId: String(check.id),
      orgId: respOrgId,
      orgName: org?.name ?? '',
      response: respText.trim(),
      notes: respNotes.trim(),
      respondedAt: new Date().toISOString(),
    });
    setSubmitting(false);
    notifications.show({ color: 'var(--color-success)', title: 'Submitted', message: 'Response submitted.' });
    setRespondCheckId(null); setRespOrgId(''); setRespText(''); setRespNotes('');
  }

  // ── Render ─────────────────────────────────────────────────

  return (
    <div>
      <Group justify="space-between" mb="md">
        <Title order={3} c="black">Status Checks</Title>
        <Group gap="xs">
          <Button size="xs" variant="light" leftSection={<IconRefresh size={14} />}
            loading={loading} onClick={() => { loadStatusChecks(); loadStatusResponses(); }}>
            Refresh
          </Button>
          {canEdit && (
            <Button size="xs" leftSection={<IconPlus size={14} />} onClick={openCreate}>
              New Status Check
            </Button>
          )}
        </Group>
      </Group>

      <Text size="xs" c="dimmed" mb="md">{filtered.length} check{filtered.length !== 1 ? 's' : ''}</Text>

      {filtered.length === 0 && !loading && (
        <Paper p="xl" withBorder>
          <Text ta="center" c="dimmed">No status checks found.</Text>
        </Paper>
      )}

      {loading && filtered.length === 0 && (
        <Group justify="center" p="xl"><Loader size="sm" /></Group>
      )}

      {filtered.map(check => {
        const responses = responsesForCheck(check.id);
        const target = targetCount(check);
        const pct = target > 0 ? Math.round((responses.length / target) * 100) : 0;
        const isExpanded = expandedIds[check.id];
        const isDeadlinePassed = check.deadline && new Date(check.deadline) < new Date();
        return (
          <Paper key={check.id} p="sm" mb="sm" withBorder
            style={{ borderLeft: `3px solid ${check.status === 'closed' ? '#939598' : isDeadlinePassed ? 'var(--color-error)' : 'var(--color-primary-700)'}` }}>
            <Group justify="space-between" wrap="nowrap">
              <div style={{ flex: 1 }}>
                <Group gap="xs" mb={4}>
                  <Text fw={600} size="sm">{check.title}</Text>
                  <Badge size="xs" color={check.status === 'open' ? 'var(--color-primary-700)' : '#939598'}>{check.status}</Badge>
                  <Badge size="xs" variant="outline" color={pct >= 100 ? 'var(--color-success)' : pct >= 50 ? '#C68700' : '#939598'}>
                    {responses.length}/{target} ({pct}%)
                  </Badge>
                  {isDeadlinePassed && check.status === 'open' && (
                    <Badge size="xs" color="var(--color-error)" variant="filled">OVERDUE</Badge>
                  )}
                </Group>
                <Text size="xs" c="dimmed">{check.question}</Text>
                <Group gap="xs" mt={4}>
                  <Text size="xs" c="dimmed">By {check.authorName}</Text>
                  {check.deadline && (
                    <Text size="xs" c={isDeadlinePassed ? 'var(--color-error)' : 'dimmed'}>
                      Deadline: {fmtDate(check.deadline)}
                    </Text>
                  )}
                  <Text size="xs" c="dimmed">{fmtDateTime(check.createdAt)}</Text>
                </Group>
              </div>
              <Group gap={4}>
                {canEdit && check.status === 'open' && (
                  <ActionIcon size="sm" variant="subtle" color="#939598" onClick={() => handleClose(check)}
                    title="Close check">
                    <IconLock size={14} />
                  </ActionIcon>
                )}
                {canEdit && (
                  <ActionIcon size="sm" variant="subtle" onClick={() => { setRespondCheckId(respondCheckId === check.id ? null : check.id); }}>
                    <IconSend size={14} />
                  </ActionIcon>
                )}
                <ActionIcon size="sm" variant="subtle" onClick={() => toggleExpand(check.id)}>
                  {isExpanded ? <IconChevronUp size={14} /> : <IconChevronDown size={14} />}
                </ActionIcon>
              </Group>
            </Group>

            {/* Respond inline */}
            <Collapse in={respondCheckId === check.id}>
              <Paper p="sm" mt="sm" bg="#F8F8F8" radius="sm">
                <Text size="xs" fw={600} mb="xs">Submit Response</Text>
                <Group gap="sm" mb="xs">
                  <Select data={orgOptions} value={respOrgId}
                    onChange={v => setRespOrgId(v ?? '')} placeholder="Select organization"
                    size="xs" searchable style={{ flex: 1 }} />
                </Group>
                <Textarea value={respText} onChange={e => setRespText(e.target.value)}
                  placeholder="Your response..." size="xs" mb="xs" autosize minRows={2} />
                <TextInput value={respNotes} onChange={e => setRespNotes(e.target.value)}
                  placeholder="Additional notes (optional)" size="xs" mb="xs" />
                <Group justify="flex-end">
                  <Button size="xs" variant="subtle" onClick={() => setRespondCheckId(null)}>Cancel</Button>
                  <Button size="xs" loading={submitting} onClick={() => handleRespond(check)}>Submit</Button>
                </Group>
              </Paper>
            </Collapse>

            {/* Responses table */}
            <Collapse in={isExpanded}>
              {responses.length === 0 ? (
                <Text size="xs" c="dimmed" mt="sm">No responses yet.</Text>
              ) : (
                <Paper mt="sm" withBorder style={{ overflow: 'hidden' }}>
                  <div style={{ overflowX: 'auto', overflowY: 'auto', maxHeight: 'calc(100vh - 300px)' }}>
                  <Table striped highlightOnHover withTableBorder={false} fz="xs">
                    <Table.Thead style={{ position: 'sticky', top: 0, zIndex: 1, background: '#F8F8F8' }}>
                      <Table.Tr>
                        <Table.Th>Organization</Table.Th>
                        <Table.Th>Response</Table.Th>
                        <Table.Th>Notes</Table.Th>
                        <Table.Th>Responded</Table.Th>
                      </Table.Tr>
                    </Table.Thead>
                    <Table.Tbody>
                      {responses.map(r => (
                        <Table.Tr key={r.id}>
                          <Table.Td>{r.orgName}</Table.Td>
                          <Table.Td>{r.response}</Table.Td>
                          <Table.Td>{r.notes || '-'}</Table.Td>
                          <Table.Td>{fmtDateTime(r.respondedAt)}</Table.Td>
                        </Table.Tr>
                      ))}
                    </Table.Tbody>
                  </Table>
                  </div>
                </Paper>
              )}
            </Collapse>
          </Paper>
        );
      })}

      {/* ── Create Modal ────────────────────────────────────── */}
      <Modal opened={createOpened} onClose={closeCreate} title="New Status Check" size="lg">
        <TextInput label="Title" value={title} onChange={e => setTitle(e.target.value)}
          placeholder="e.g. Post-Storm Operational Status" size="sm" mb="sm" required />
        <Textarea label="Question" value={question} onChange={e => setQuestion(e.target.value)}
          placeholder="What is the current operational status of your organization?"
          size="sm" mb="sm" autosize minRows={3} required />
        <Select label="Target Type" data={[
          { value: 'all', label: 'All Organizations' },
          { value: 'sector', label: 'By Sector' },
          { value: 'list', label: 'By List' },
          { value: 'specific', label: 'Specific Organizations' },
        ]} value={targetType} onChange={v => setTargetType(v ?? 'all')} size="sm" mb="sm" />

        {targetType === 'sector' && (
          <MultiSelect label="Target Sectors" data={SECTOR_OPTIONS}
            value={targetSectors} onChange={setTargetSectors}
            placeholder="Select sectors" size="sm" mb="sm" searchable />
        )}
        {targetType === 'list' && (
          <Select label="Target List" placeholder="Select a list..."
            data={orgLists.map(l => ({ value: String(l.id), label: `${l.name} (${l.orgIds.split(',').filter(Boolean).length} orgs)` }))}
            value={targetList} onChange={setTargetList}
            size="sm" mb="sm" />
        )}
        {targetType === 'specific' && (
          <MultiSelect label="Target Organizations" data={orgOptions}
            value={targetOrgs} onChange={setTargetOrgs}
            placeholder="Select organizations" size="sm" mb="sm" searchable />
        )}

        <TextInput label="Deadline" type="date" value={deadline}
          onChange={e => setDeadline(e.target.value)} size="sm" mb="md" />

        <Group justify="flex-end">
          <Button variant="subtle" onClick={closeCreate}>Cancel</Button>
          <Button loading={saving} onClick={handleCreate}
            leftSection={<IconClipboardCheck size={16} />}>Create Check</Button>
        </Group>
      </Modal>
    </div>
  );
}
